import { useSelector } from 'react-redux';
import { GenerateReportRequest } from 'features/lead-management/types/request';

import { SelectedItemsObj } from '@components/multi-selector/types';
import { TimeZones } from '@constants/date';
import {
  SubscriptionPlanExpiredMessage,
  SubscriptionStatus
} from '@constants/generic';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import {
  Event,
  MixpanelEventV2,
  sendMixpanelEvent,
  sendMixpanelEventV2
} from '@core/analytics/mixpanel';
import { useAppSelector } from '@core/store';
import { formatDateRange } from '@features/group-management/helper';
import { ApiError, DateRange } from '@types';
import { formatISODate, getFormattedDateTimeWithOffset } from '@utils/date';
import { buildQueryString } from '@utils/queryStringBuilder';
import { getLocalStorageItem } from '@utils/storage';

import {
  useGenerateLeadReportMutation,
  useGetCustomReportJobIdV3Mutation,
  useLazyGetAuditLogJobIdQuery,
  useLazyGetChargesReportJobIdQuery,
  useLazyGetGstReportJobIdQuery
} from '../queries/ReportsQuery';
import { buildCustomReportV3Body } from '../utils/customReportBody';
import useReportJob from './useReportJob';

const formattedDate = (date: string | Date, isEndDate = false) =>
  getFormattedDateTimeWithOffset(date, TimeZones['Asia/Kolkata'], isEndDate);

const useDownloadReport = () => {
  const {
    createErrorAlert,
    downloadTrialReport,
    handleReportJobError,
    pollLeadReportJobStatus,
    redirectToPlanDetailsIfPermitted,
    registerDownload,
    trackReportJob
  } = useReportJob();
  const [getGstReportJobId] = useLazyGetGstReportJobIdQuery();
  const [getCustomReportJobIdV3] = useGetCustomReportJobIdV3Mutation();
  const [getChargesReportJobId] = useLazyGetChargesReportJobIdQuery();
  const [getAuditLogJobId] = useLazyGetAuditLogJobIdQuery();
  const [getReportJobStatus] = useGenerateLeadReportMutation();

  const { currentSubscription } = useSelector((state: any) => state.user);
  const isTrialMode = useAppSelector((state) => state.trial.isTrialMode);
  const organizationId = getLocalStorageItem(
    LOCAL_STORAGE_KEYS.ORGANIZATION_ID
  );

  const handleDownloadGstReport = async (
    selectedDateRange: DateRange,
    selectedBranchIds: string[],
    defaultBranchId: string,
    isMarkAsPaidChecked: boolean,
    branchNames: string[] = [],
    globalFields: string[] = [],
    memberFields: string[] = [],
    customFields: string[] = [],
    tagIds: number[] = [],
    columnTagIds: string[] = []
  ) => {
    const dateRange = formatDateRange(selectedDateRange);
    const { startDate, endDate } = dateRange;
    const downloadId = `gst-report-${Date.now()}`;

    if (isTrialMode) {
      await downloadTrialReport(
        'generateTrialGstReport',
        downloadId,
        'GST_Report',
        dateRange
      );
      return;
    }

    const queryString = buildQueryString({
      from_timestamp: formattedDate(startDate),
      to_timestamp: formattedDate(endDate, true),
      branch_id: selectedBranchIds.join(','),
      payment_mode: isMarkAsPaidChecked ? 'all' : 'automatic',
      ...(globalFields.length ? { global_fields: globalFields.join(',') } : {}),
      ...(memberFields.length ? { member_fields: memberFields.join(',') } : {}),
      ...(customFields.length ? { custom_fields: customFields.join(',') } : {}),
      ...(tagIds.length ? { tag_ids: tagIds.join(',') } : {}),
      ...(columnTagIds.length ? { column_tag_ids: columnTagIds.join(',') } : {})
    });

    const res = await getGstReportJobId({
      params: { organizationId, branchId: defaultBranchId },
      queryString
    });

    if (res?.data) {
      const jobId = res.data?.job_id;
      if (jobId) {
        await trackReportJob({
          jobId,
          downloadId,
          mixpanelData: {
            fileType: 'gst_report',
            selectedBranchIds,
            includeMarkAsPaidChecked: isMarkAsPaidChecked
          }
        });
      }
      sendMixpanelEvent(Event.DOWNLOAD_GST_REPORT_CLICKED, {
        startDate,
        endDate,
        selectedBranchIds,
        includeMarkAsPaidChecked: isMarkAsPaidChecked
      });
      sendMixpanelEventV2(MixpanelEventV2.REPORT_DOWNLOADED, {
        report_type: 'GST Report',
        date_range: { start_date: startDate, end_date: endDate },
        branch_name: branchNames.join(', '),
        include_mark_as_paid: isMarkAsPaidChecked
      });
    } else if ('error' in res) {
      const message = handleReportJobError(res.error as ApiError, downloadId);
      sendMixpanelEvent(Event.DOWNLOAD_GST_REPORT_CLICKED, {
        error: message
      });
    }
  };

  const handleDownloadCustomReport = async (
    selectedDateRange: DateRange,
    selectedBranchIds: string[],
    defaultBranchId: string,
    isQuickCollectChecked: boolean,
    selectCategory: string,
    groupBy: string,
    branchNames: string[] = [],
    selectedGroups: SelectedItemsObj = {},
    allGroupsByBranch: SelectedItemsObj = {},
    customFields: string[] = [],
    globalFields: string[] = [],
    memberFields: string[] = [],
    counsellorNames: string[] | null = null,
    reportType: 'branch' | 'organisation' = 'branch',
    tagIds: number[] = [],
    columnTagIds: string[] = []
  ) => {
    const dateRange = formatDateRange(selectedDateRange);
    const { startDate, endDate } = dateRange;
    const downloadId = `custom-report-${Date.now()}`;

    if (isTrialMode) {
      await downloadTrialReport(
        'generateTrialCustomReport',
        downloadId,
        'Custom_Report',
        dateRange
      );
      return;
    }

    const requestBody = buildCustomReportV3Body({
      fromTimestamp: formattedDate(startDate),
      toTimestamp: formattedDate(endDate, true),
      selectedBranchIds,
      selectedGroups,
      allGroupsByBranch,
      includeMarkAsPaid: true,
      includeQuickCollect: isQuickCollectChecked,
      includeTaxInfo: false,
      category: selectCategory,
      groupBy,
      paymentMode: '',
      customFields,
      globalFields,
      memberFields,
      counsellorNames,
      reportType,
      tagIds,
      columnTagIds
    });

    const res = await getCustomReportJobIdV3({
      params: {
        organizationId,
        branchId: defaultBranchId || selectedBranchIds[0] || ''
      },
      body: requestBody
    });
    if ('data' in res) {
      const jobId = res.data?.job_id;
      if (jobId) {
        await trackReportJob({
          jobId,
          downloadId,
          mixpanelData: {
            fileType: 'custom_report',
            startDate,
            endDate,
            selectedBranchIds,
            includeMarkAsPaidChecked: true
          }
        });
      }
      sendMixpanelEvent(Event.DOWNLOAD_CUSTOM_REPORT_CLICKED, {
        startDate,
        endDate,
        selectedBranchIds,
        includeMarkAsPaidChecked: true,
        isQuickCollectChecked,
        categorySelected: selectCategory
      });
      sendMixpanelEventV2(MixpanelEventV2.REPORT_DOWNLOADED, {
        report_type: 'Collections Report',
        date_range: { start_date: startDate, end_date: endDate },
        branch_name: branchNames.join(', '),
        include_mark_as_paid: true,
        include_quick_collect: isQuickCollectChecked,
        filter_by_category: selectCategory
      });
    } else if ('error' in res) {
      const message = handleReportJobError(res.error as ApiError, downloadId);
      sendMixpanelEvent(Event.DOWNLOAD_CUSTOM_REPORT_ERROR, {
        error: message
      });
    }
  };

  const handleDownloadChargesReport = async (
    selectedDateRange: DateRange,
    selectedBranchIds: string[],
    defaultBranchId: string,
    branchNames: string[] = []
  ) => {
    const { startDate, endDate } = formatDateRange(selectedDateRange);
    const queryString = buildQueryString({
      from_timestamp: formattedDate(startDate),
      to_timestamp: formattedDate(endDate, true),
      branch_id: selectedBranchIds.join(',')
    });
    const downloadId = `charges-report-${Date.now()}`;

    const res = await getChargesReportJobId({
      params: {
        organizationId,
        branchId:
          selectedBranchIds.length === 1
            ? selectedBranchIds[0]
            : defaultBranchId
      },
      queryString
    });

    if (res?.data) {
      const jobId = res.data?.job_id;
      if (jobId) {
        await trackReportJob({
          jobId,
          downloadId,
          mixpanelData: {
            fileType: 'charges_report',
            startDate,
            endDate,
            selectedBranchIds
          }
        });
      }
      sendMixpanelEvent(Event.DOWNLOAD_CHARGES_REPORT_CLICKED, {
        startDate,
        endDate,
        selectedBranchIds
      });
      sendMixpanelEventV2(MixpanelEventV2.REPORT_DOWNLOADED, {
        report_type: 'Transaction Charges Report',
        date_range: { start_date: startDate, end_date: endDate },
        branch_name: branchNames.join(', ')
      });
    } else if ('error' in res) {
      const message = handleReportJobError(res.error as ApiError, downloadId);
      sendMixpanelEvent(Event.DOWNLOAD_CHARGES_REPORT_ERROR, {
        error: message
      });
    }
  };

  // Omitting branchId exports the audit log for the whole organization
  const handleDownloadAuditLog = async (
    selectedDateRange: DateRange,
    branchId?: string
  ) => {
    const { startDate, endDate } = formatDateRange(selectedDateRange);
    const queryString = buildQueryString({
      from_timestamp: formattedDate(startDate),
      to_timestamp: formattedDate(endDate, true),
      ...(branchId ? { branch_id: branchId } : {})
    });
    const selectedBranchIds = branchId ? [branchId] : [];
    const downloadId = `audit-log-${Date.now()}`;

    const res = await getAuditLogJobId({ organizationId, queryString });

    if (res.data) {
      const jobId = res.data.job_id;
      if (jobId) {
        await trackReportJob({
          jobId,
          downloadId,
          fileName: `Audit_Log_${formatISODate({
            date: new Date(),
            format: 'YYYY-MM-DD_HHMMSS'
          })}.xlsx`,
          mixpanelData: {
            fileType: 'audit_log',
            startDate,
            endDate,
            selectedBranchIds
          }
        });
      }
      sendMixpanelEvent(Event.DOWNLOAD_AUDIT_LOG_CLICKED, {
        startDate,
        endDate,
        selectedBranchIds
      });
      sendMixpanelEventV2(MixpanelEventV2.REPORT_DOWNLOADED, {
        report_type: 'Audit Log',
        date_range: { start_date: startDate, end_date: endDate }
      });
    } else if ('error' in res) {
      const message = handleReportJobError(res.error as ApiError, downloadId);
      sendMixpanelEvent(Event.DOWNLOAD_AUDIT_LOG_ERROR, {
        error: message
      });
    }
  };

  const handleDownloadLeadReport = async (
    orgId: string,
    payload: GenerateReportRequest
  ) => {
    if (currentSubscription?.status === SubscriptionStatus.EXPIRED) {
      createErrorAlert(SubscriptionPlanExpiredMessage);
      redirectToPlanDetailsIfPermitted();
      return;
    }

    const downloadId = `${payload.report_type}-${Date.now()}`;
    const result = await getReportJobStatus({ organizationId: orgId, payload });
    const jobId = 'data' in result ? result.data?.job_id : undefined;
    if (jobId) {
      registerDownload(downloadId);
      await pollLeadReportJobStatus({ jobId, downloadId });
    }
  };

  return {
    handleDownloadGstReport,
    handleDownloadCustomReport,
    handleDownloadChargesReport,
    handleDownloadAuditLog,
    handleDownloadLeadReport
  };
};

export default useDownloadReport;
